import React from "react"
import PropTypes from "prop-types"
import { Button, Empty, Flex, Modal, Skeleton } from "antd"
import { useTranslation } from "react-i18next"
import moment from "moment"
import { useDevice } from "hooks/useDevice"
import CustomDrawer from "ui/Drawer"
import { Text } from "ui/Typography"
import { BLACKOUT_SCOPES } from "helpers/blackouts"
import { useReplaceResources } from "./useReplaceResources"
import ReplacePicker from "./ReplacePicker"
import styles from "./index.module.css"

/** Scope -> the sheet's own copy. Crew and equipment differ in every noun, so nothing is shared. */
const SCOPE_COPY = {
  [BLACKOUT_SCOPES.CREW]: {
    title: { key: "calendar.blackout.replace_crew", fallback: "Replace Crew" },
    description: { key: "calendar.blackout.replace_crew_description", fallback: "Replace crew for affected bookings" },
    placeholder: { key: "calendar.blackout.replace_crew_placeholder", fallback: "Select crew" },
    empty: { key: "calendar.blackout.replace_crew_none", fallback: "No other crew member is free for these dates." }
  },
  [BLACKOUT_SCOPES.EQUIPMENT]: {
    title: { key: "calendar.blackout.replace_equipment", fallback: "Replace Equipment" },
    description: {
      key: "calendar.blackout.replace_equipment_description",
      fallback: "Replace equipment for affected bookings"
    },
    placeholder: { key: "calendar.blackout.replace_equipment_placeholder", fallback: "Select equipment" },
    empty: { key: "calendar.blackout.replace_equipment_none", fallback: "No other item is free for these dates." }
  }
}

/** "June 9" — the row's meta line names the month and day, never the year. */
const DATE_FORMAT = "MMMM D"

/**
 * "Leslie Alexander (Capitan)".
 *
 * The category is appended rather than assumed: crew sometimes arrives from the API with it
 * already bound into `label`, and printing it twice is worse than leaving it off, so a label
 * that already ends in a parenthesis is taken as complete.
 */
const formatResourceLabel = (resource) => {
  const label = resource?.label || ""
  if (!resource?.category || label.endsWith(")")) return label
  return `${label} (${resource.category})`
}

/**
 * When a crew member or a piece of equipment is blacked out, the bookings it was carrying are
 * left uncovered. This sheet is where they are handed to something else.
 *
 * One row per stranded booking, each with its own picker, because the bookings do not have to go
 * to the same place. Bookings left unpicked are simply not sent, so a guide can reassign two
 * today and the rest later.
 */
const ReplaceResourceModal = ({ isOpen, scope, blackoutId, onCancel, onReplaced }) => {
  const { t } = useTranslation()
  const { isMobile } = useDevice()
  const copy = SCOPE_COPY[scope]

  const { bookings, isLoading, hasFailed, isSaving, assignments, assignedCount, assignOne, submit } =
    useReplaceResources({ blackoutId, scope, isOpen })

  const handleReplace = () => {
    submit()
      .then(() => {
        onReplaced?.()
        onCancel()
      })
      // The api() helper surfaces the failure itself; the sheet stays open so the picks survive.
      .catch(() => {})
  }

  /**
   * Options belong to the row, not the sheet: `check_impact` answers availability per booking, so
   * a crew member offered for Tuesday's trip can be absent from Thursday's.
   */
  const getCandidateOptions = (booking) =>
    (booking.candidates || []).map((candidate) => ({
      value: candidate.id,
      label: formatResourceLabel(candidate),
      name: candidate.label,
      category: candidate.category,
      searchText: formatResourceLabel(candidate).toLowerCase()
    }))

  /** "#CZUHY • June 9, 08:00 AM - 10:00 AM" — the time half is dropped for an all-day booking. */
  const formatMeta = (booking) => {
    const date = booking.start_date ? moment(booking.start_date).format(DATE_FORMAT) : ""
    const time = [booking.start_time, booking.end_time].filter(Boolean).join(" - ")
    const when = [date, time].filter(Boolean).join(", ")
    return [booking.booking_id && `#${booking.booking_id}`, when].filter(Boolean).join(" • ")
  }

  const renderRow = (booking) => {
    const options = getCandidateOptions(booking)
    return (
      <div key={booking.id} className={styles.row}>
        <div className={styles.bookingCell}>
          <span className={styles.bookingTitle}>{booking.trip_title}</span>
          <span className={styles.bookingMeta}>{formatMeta(booking)}</span>
        </div>
        <span className={styles.resourceCell}>{formatResourceLabel(booking.resource)}</span>
        <ReplacePicker
          value={assignments[booking.id]}
          options={options}
          placeholder={
            options.length
              ? t(copy.placeholder.key, { defaultValue: copy.placeholder.fallback })
              : t(copy.empty.key, { defaultValue: copy.empty.fallback })
          }
          onChange={(candidateId) => assignOne(booking.id, candidateId)}
          isDisabled={!options.length}
        />
      </div>
    )
  }

  const content = (
    <div className={styles.content}>
      <Text variant="sub" size="md" className={styles.description}>
        {t(copy.description.key, { defaultValue: copy.description.fallback })}
      </Text>

      {isLoading && <Skeleton active paragraph={{ rows: 4 }} />}

      {!isLoading && hasFailed && (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={t("calendar.blackout.replace_failed", { defaultValue: "Could not load the affected bookings. Try again." })}
        />
      )}

      {!isLoading && !hasFailed && !bookings.length && (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={t("calendar.blackout.replace_no_bookings", { defaultValue: "No bookings need reassigning." })}
        />
      )}

      {!isLoading && !hasFailed && bookings.length > 0 && (
        <div className={styles.table}>
          <div className={`${styles.row} ${styles.headerRow}`}>
            <span>{t("calendar.blackout.replace_column_booking", { defaultValue: "Booking" })}</span>
            <span>{t("calendar.blackout.replace_column_resource", { defaultValue: "Affected Resource" })}</span>
            <span>{t("calendar.blackout.replace_column_replacement", { defaultValue: "Replace with" })}</span>
          </div>

          {bookings.map(renderRow)}
        </div>
      )}
    </div>
  )

  const footer = (
    <Flex gap="var(--spacing-s)" align="center" className={styles.footer}>
      <Button onClick={onCancel} disabled={isSaving}>
        {t("global.cancel", { defaultValue: "Cancel" })}
      </Button>
      <Button type="primary" onClick={handleReplace} loading={isSaving} disabled={!assignedCount}>
        {t("calendar.blackout.replace_confirm", { defaultValue: "Replace" })}
      </Button>
    </Flex>
  )

  const title = t(copy.title.key, { defaultValue: copy.title.fallback })

  return isMobile ? (
    <CustomDrawer open={isOpen} setOpen={onCancel} title={title} footer={footer} destroyOnClose className={styles.sheet}>
      {content}
    </CustomDrawer>
  ) : (
    <Modal open={isOpen} onCancel={onCancel} title={title} width={800} centered destroyOnClose footer={footer} className={styles.modal}>
      {content}
    </Modal>
  )
}

ReplaceResourceModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  /** Only "crew" and "equipment" have resources to reassign. */
  scope: PropTypes.oneOf([BLACKOUT_SCOPES.CREW, BLACKOUT_SCOPES.EQUIPMENT]).isRequired,
  blackoutId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onCancel: PropTypes.func.isRequired,
  /** Fired after a successful save, so the caller can refetch whatever showed the conflict. */
  onReplaced: PropTypes.func
}

export default ReplaceResourceModal
